'use client'

import { QueryKey } from '@tanstack/react-query'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable,
  getSortedRowModel,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ReactNode, useRef, useMemo, useEffect } from 'react'

import { EmptyTable } from './EmptyTable'
import { ExportCsvButton } from './ExportCsvButton'
import { useHoverColumnInView } from './hooks/useHoverColumnInView'
import { TableSelectionProps, useTableSelection } from './hooks/useTableSelection'
import { useTableSelectionColumns } from './hooks/useTableSelectionColumns'
import { useTableSelectionRef } from './hooks/useTableSelectionRef'
import { useVisibleRows, type VisibleRowsChangeHandler } from './hooks/useVisibleRows'
import { TableRow, tablePaddingClass } from './InfiniteTableRow'
import { RenderCount } from './RenderCount'
import { InfinitePage, useInfiniteTableData } from './useInfiniteTableData'
import { useTableSorting } from './useTableSorting'
import { AllFiltersArea } from '@components/Filters/filter.types'
import { ScrollFadeOverlay, Spin, useScrollFade } from '@fp/ui-kit'
import { cn } from '@lib/cn'
import { useDebugSettings } from '@lib/debugSettings'
import { getSortingIcon } from '@lib/sortingHelper'
import { useAppSelector } from '@store/hooks'
import { selectFilterCount } from '@store/modules/filters/filterMetaSlice'
import { InfiniteScrollSentinel } from '@ui/InfiniteScrollSentinel'

/*
 Dynamic row height example:
 https://github.com/TanStack/table/blob/main/examples/react/virtualized-rows/src/main.tsx
*/

const OVERSCAN = 10 // Optimized to 10 from 50 because of MultiSelect and InputWithDropdown

interface InfiniteTableProps<T extends RowData, Q> extends TableSelectionProps<T> {
  tableId?: string

  /** Column definition (same as before) */
  columns: ColumnDef<T>[]
  /** Your page fetcher (path + query hidden inside) */
  fetchPage: (p: Q) => Promise<InfinitePage<T>>
  getQueryKey?: (query: Q) => QueryKey
  refetchInterval?: number | false

  /** Filter / sort / etc */
  query?: Q

  /** Optional top-bar override */
  headerContent?: ReactNode
  tableTitle?: string
  /** Rendered inline next to `tableTitle` — use for contextual chips/badges. */
  titleSuffix?: ReactNode

  /** Counters */
  showItemsCount?: boolean
  filterArea?: AllFiltersArea
  totalCount?: number
  countLoading?: boolean

  /** CSV */
  csvExportingHandle?: () => Promise<void>
  csvExportingIsLoading?: boolean

  /** Table viewport height */
  height?: number | string

  /** Auto height for table rows */
  isRowAutoHeight?: boolean

  /** Row height guess */
  estimatedRowHeight?: number

  /** Enable sorting */
  enableSorting?: boolean

  /** Server side sorting */
  isServerSorting?: boolean

  footer?: ReactNode

  /** Table container class name */
  className?: string
  getRowClassName?: (row: T) => string
  emptyText?: string

  /** On visible rows change */
  onVisibleRowsChange?: VisibleRowsChangeHandler<T>
}

const stickyClass = 'sticky top-0 z-20 bg-background py-1 rounded-xl border border-border'

export const InfiniteTable = <T extends RowData, Q>({
  tableId,
  columns,
  fetchPage,
  query = {} as Q,
  refetchInterval,
  getQueryKey = () => [' '],

  headerContent,
  tableTitle,
  titleSuffix,
  showItemsCount = false,
  filterArea,
  totalCount: controlledTotalCount,

  csvExportingHandle,

  height,
  isRowAutoHeight = true,
  estimatedRowHeight = 46,

  isServerSorting = true,
  enableSorting = true,

  footer,
  className = '',

  getRowClassName,

  emptyText = 'No data',
  onVisibleRowsChange,

  enableRowSelection = false,
  defaultRowSelection = {},
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  getRowId,

  ref,
}: InfiniteTableProps<T, Q>) => {
  const filterCountSelector = useMemo(() => selectFilterCount(filterArea), [filterArea])
  const filterAreaCount = useAppSelector(filterCountSelector)
  const totalCount = controlledTotalCount ?? filterAreaCount

  // Dev-only render-count badges, gated by the debug setting (one subscription per table).
  const { renderCounters } = useDebugSettings()

  const { queryWithSorting, sorting, setSorting } = useTableSorting({
    query,
    isServerSorting,
    paramPrefix: tableId,
  })

  const { items, meta, isLoading, fetchNext } = useInfiniteTableData<T, typeof queryWithSorting>({
    fetchPage,
    query: queryWithSorting,
    queryKey: getQueryKey(queryWithSorting),
    refetchInterval,
  })

  const { rowSelection, setRowSelection } = useTableSelection({
    rowSelection: controlledRowSelection,
    onRowSelectionChange,
    defaultRowSelection,
  })
  const tableStateVersion = enableRowSelection ? rowSelection : undefined

  const tableColumns = useTableSelectionColumns({
    enableRowSelection,
    columns,
    orderableFields: meta.orderable_fields,
    setRowSelection,
  })

  /* eslint-disable react-hooks/incompatible-library */
  const table = useReactTable({
    data: items,
    columns: tableColumns,

    state: {
      sorting,
      ...(enableRowSelection ? { rowSelection } : {}),
    },

    onSortingChange: (up) => {
      setSorting(up)
    },
    enableSorting,
    manualSorting: isServerSorting, // true - server side sorting
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    getRowId,

    defaultColumn: {
      size: 150,
      minSize: 100,
      maxSize: 4000,
      enableSorting: false,
    },
  })

  useTableSelectionRef({ ref, table, rowSelection, setRowSelection })

  /* -------------------------------- virtualization */
  const scrollRef = useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight } = useScrollFade(scrollRef)
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: OVERSCAN,
    measureElement:
      // Measure dynamic row height, except in firefox because it measures table border height incorrectly
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1 && isRowAutoHeight
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()

  useVisibleRows(virtualRows, table, onVisibleRowsChange)

  const { isHoverColumnInView, hoverColumnHeaderRef } = useHoverColumnInView(scrollRef)

  const hasHoverColumn = useMemo(() => tableColumns.some((col) => col.meta?.isHover), [tableColumns])

  // Sync the visible right-edge position as a CSS custom property on the scroll container.
  // Child elements read it via var(--hover-anchor-right) — no React state, no re-renders.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !hasHoverColumn) return

    const update = () => {
      el.style.setProperty('--hover-anchor-right', `${el.scrollLeft + el.clientWidth}px`)
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)

    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [hasHoverColumn])

  const tableScrollStyle = useMemo(() => ({ height: height ? `${height}` : 'auto' }), [height])
  const totalSize = rowVirtualizer.getTotalSize()
  const vTableHeaderSize = useMemo(() => ({ height: `${totalSize}px` }), [totalSize])
  const tableHeaderGroups = table.getHeaderGroups()[0]

  // Build grid template dynamically
  const columnSizes = useMemo(
    () =>
      tableHeaderGroups?.headers.map((col) => {
        // By default, all columns are dynamic
        const isDynamic = col.column.columnDef.meta?.isDynamic ?? true

        if (isDynamic) {
          // Get minSize from size props
          const minSize = col.getSize() || 100

          const min = col.column.columnDef.minSize ?? minSize
          const max = col.column.columnDef.maxSize ?? '1fr' // if not set, it can expand freely

          return `minmax(${min}px, ${typeof max === 'number' ? `${max}px` : max})`
        } else {
          return `${col.getSize()}px`
        }
      }),
    [tableHeaderGroups],
  )

  const gridTemplateColumns = columnSizes?.join(' ') || ''

  const tHeadStyle = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns,
      boxShadow: '-4px -4px 0px var(--background), 4px -4px 0px var(--background)',
    }),
    [gridTemplateColumns],
  )

  const DefaultHeader = useMemo(
    () =>
      (tableTitle || headerContent) && (
        <div className={`${stickyClass} flex justify-between items-center p-2 pr-1 w-full mt-4 mb-2 gap-3`}>
          {tableTitle && (
            <div className={`flex items-center gap-3 h-full mr-12`}>
              <h2 className='text-lg font-semibold content-center h-full'>{tableTitle}</h2>

              {showItemsCount && (
                <div className='flex whitespace-nowrap items-center text-center gap-2 text-sm text-muted-foreground h-full mt-[3px] tabular-nums'>
                  <span>{`${items.length}${totalCount ? ` / ${totalCount}` : ''}`}</span>

                  <span aria-hidden className='inline-flex size-4 items-center justify-center'>
                    {isLoading && <Spin size='sm' />}
                  </span>
                </div>
              )}

              {titleSuffix}
            </div>
          )}

          {headerContent}
          {csvExportingHandle && <ExportCsvButton csvExportingHandle={csvExportingHandle} />}
        </div>
      ),
    [tableTitle, titleSuffix, showItemsCount, items.length, totalCount, isLoading, headerContent, csvExportingHandle],
  )

  const trRefAutoHeight = useMemo(
    () => (isRowAutoHeight ? rowVirtualizer.measureElement : undefined),
    [isRowAutoHeight, rowVirtualizer.measureElement],
  )

  return (
    <div className={cn('relative flex flex-1 flex-col overflow-auto h-full w-full p-0 animate-fade-in', className)}>
      {renderCounters && <RenderCount variant='table' />}
      {DefaultHeader}

      <div ref={scrollRef} className={`relative overflow-auto`} style={tableScrollStyle}>
        <table className='relative grid min-h-fit'>
          <thead style={tHeadStyle} className={cn(`grid mb-2`, stickyClass, tablePaddingClass)}>
            {table.getHeaderGroups().map((hg) => (
              <tr key={`${hg.id}-header`} className='contents'>
                {hg.headers.map((headerCell) => {
                  const textAlign = headerCell.column.columnDef.meta?.textAlign ?? 'left'
                  const textAlignClass =
                    textAlign === 'center'
                      ? 'justify-center text-center'
                      : textAlign === 'right'
                        ? 'justify-end text-right'
                        : 'justify-start text-left'
                  const canSort = headerCell.column.getCanSort()
                  const isSorted = headerCell.column.getIsSorted()

                  const isHoverCol = headerCell.column.columnDef.meta?.isHover

                  return (
                    <th
                      key={`${hg.id}-${headerCell.index}-header`}
                      ref={isHoverCol ? hoverColumnHeaderRef : undefined}
                      onClick={canSort ? headerCell.column.getToggleSortingHandler() : undefined}
                      className={cn(
                        `py-1 px-1 text-left text-xs font-medium text-muted-foreground uppercase flex items-center gap-1`,
                        textAlignClass,
                        canSort ? 'cursor-pointer' : 'cursor-default',
                      )}
                    >
                      {flexRender(headerCell.column.columnDef.header, headerCell.getContext())}
                      {getSortingIcon(isSorted, canSort)}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>

          <tbody className='block relative min-h-fit overflow-auto transform-gpu' style={vTableHeaderSize}>
            {items.length > 0 &&
              virtualRows.map((vRow) => {
                const row = table.getRowModel().rows[vRow.index]

                if (!row) return null
                const index = vRow.index

                const rowClassName = getRowClassName ? getRowClassName(row.original) : 'hover:bg-muted'

                return (
                  <TableRow
                    key={`${row.id}-row`}
                    rowId={row.id}
                    cells={row.getVisibleCells()}
                    start={vRow.start}
                    index={index}
                    gridTemplateColumns={gridTemplateColumns}
                    rowClassName={rowClassName}
                    isHoverColumnInView={isHoverColumnInView}
                    hasHoverColumn={hasHoverColumn}
                    measureRef={trRefAutoHeight}
                    showRenderCount={renderCounters}
                    tableStateVersion={tableStateVersion}
                  />
                )
              })}

            {items.length === 0 && (
              <EmptyTable isLoading={isLoading} columnsLength={tableColumns.length} emptyText={emptyText} />
            )}
          </tbody>

          {!isLoading && items.length > 0 && (!meta || !meta.has_next) && (
            <tfoot className='w-full'>
              <tr className='flex flex-1 text-sm mt-1 col-span-full'>
                <td className='flex flex-1 border border-input justify-center w-full text-muted-foreground bg-background px-8 py-1 rounded-lg'>
                  No more records
                </td>
              </tr>
            </tfoot>
          )}
        </table>

        <InfiniteScrollSentinel
          rootRef={scrollRef}
          hasNextPage={meta?.has_next}
          onLoadMore={fetchNext}
          itemCount={items.length}
        />
      </div>
      <ScrollFadeOverlay
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        gradientFrom='from-[var(--background)]'
        size='lg'
      />
      {footer}
    </div>
  )
}
